import Papa from 'papaparse'
import { useCallback, useEffect, useMemo, useState } from 'react'
import {
  createImportPreview,
  updateImportPreview,
  useGetImportPreview,
  performMatch,
  performSetCompanies,
  performSetTags,
  performSetDeals,
  validateDealAccountOwners,
  validateDealSalesStages,
  performSetContacts,
  performSetCompanyNotes,
  usePollStatus,
  setUnmatchedChains,
} from './importer-api'
import {
  ColumnType,
  ColumnColors,
  ColumnOption,
  CSVRow,
  ImportData,
  CSVRowState,
  ImportDataRows,
} from './importer-types'
import { ChainSearchResult, DoorSearchResult } from 'features/elastic/searchers'
import { useNavigate } from 'react-router-dom'
import apiService from 'services/api'
import { toast } from 'react-toastify'
import {
  FB_REMOVE_CELL_VALUE,
  ValidateModalData,
  ValidateModalRow,
} from './validate-modal'
import { ColumnFilter, RowSelectionState } from '@tanstack/react-table'
import confirm from 'components/dialogConfirm'
import { ExportModalColumn } from 'components/Modals/ExportModal/ExportModal'

export const useImporterViewModel = (props: {
  importNumber: number | null
}) => {
  const navigate = useNavigate()
  const api = apiService()

  const { data: companyCategories } = api.useGetCompanyCategories()
  const { data: salesStages } = api.useGetSalesStages()
  const { data: users } = api.useGetUsers()
  const { data: products } = api.useGetProducts()
  const {
    data: importPreview,
    isPending: isLoading,
    isFetching: isFetching,
    refetch,
  } = useGetImportPreview(props.importNumber)
  const {
    data: importPreviewStatus,
    isPending: isLoadingStatus,
    refetch: refetchStatus,
  } = usePollStatus(props.importNumber, importPreview?.status === 'running')

  const [showSelectDealProductModal, setShowSelectDealProductModal] =
    useState(false)
  const [selectedDealProduct, setSelectedDealProduct] = useState<string | null>(
    null
  )
  const [isSaving, setIsSaving] = useState(false)
  const [isUploading, setIsUploading] = useState(false)
  const [isMatching, setIsMatching] = useState(false)
  const [isSettingCompanies, setIsSettingCompanies] = useState(false)
  const [isSettingTags, setIsSettingTags] = useState(false)
  const [isSettingDeals, setIsSettingDeals] = useState(false)
  const [isSettingContacts, setIsSettingContacts] = useState(false)
  const [isSettingCompanyNotes, setIsSettingCompanyNotes] = useState(false)
  const [isSettingUnmatchedChains, setIsSettingUnmatchedChains] =
    useState(false)
  const [importData, setImportData] = useState<ImportData | null>(null)
  const [rawFile, setRawFile] = useState<File | null>(null)
  const [hasPendingChanges, setHasPendingChanges] = useState(false)
  const [selectedTab, setSelectedTab] = useState<CSVRowState>('unmatched')
  const [selectedRowId, setSelectedRowId] = useState<string | null>(null)
  const [rowSelection, setRowSelection] = useState<RowSelectionState>({})
  const [validateModalData, setValidateModalData] =
    useState<ValidateModalData | null>(null)
  const [columnFiltering, setColumnFiltering] = useState<ColumnFilter[]>([])
  const [showHeaderMappingModal, setShowHeaderMappingModal] = useState(false)
  const [showExportModal, setShowExportModal] = useState(false)
  const [showConfigureExportModal, setShowConfigureExportModal] =
    useState(false)

  type ExportConfig = {
    allowSuggestions: boolean
    suggestionAmount: number
  }
  const [exportConfig, setExportConfig] = useState<ExportConfig>({
    allowSuggestions: true,
    suggestionAmount: 1,
  })

  /* -------------
  Computed Values
  ------------- */
  const tableData = useMemo<Record<CSVRowState, CSVRow[]>>(() => {
    if (!importData)
      return {
        unmatched: [],
        matched: [],
        set: [],
      }

    const set = importData.rows.filter(
      (r) =>
        r.__internal__set_chain_proxy_id !== -1 || r.__internal__is_chain_merged
    )

    const unmatched = importData.rows.filter(
      (r) =>
        !r.__internal__ready &&
        r.__internal__set_chain_proxy_id == -1 &&
        !r.__internal__is_chain_merged
    )

    const matched = importData.rows.filter(
      (r) =>
        r.__internal__ready &&
        r.__internal__set_chain_proxy_id == -1 &&
        !r.__internal__is_chain_merged
    )

    return {
      unmatched: unmatched,
      matched: matched,
      set: set,
    }
  }, [importData])

  const exportColumns = useMemo<ExportModalColumn[]>(() => {
    if (!importData) return []

    function toTitle(key: string) {
      return key
        .replace(/_/g, ' ')
        .replace(/\b\w/g, (char) => char.toUpperCase())
    }

    const baseColumns: ExportModalColumn['elements'] = []
    importData.headers.forEach((h) => {
      baseColumns.push([h.mapped, h.original])
    })

    const suggestionColumns: ExportModalColumn['elements'] = []
    chainSuggestionKeys.forEach((key) => {
      suggestionColumns.push([
        `__suggestion__chain__${key}`,
        `Chain ${toTitle(key)}`,
      ])
    })
    doorSuggestionKeys.forEach((key) => {
      suggestionColumns.push([
        `__suggestion__door__${key}`,
        `Door ${toTitle(key)}`,
      ])
    })

    const generatedColumns: ExportModalColumn['elements'] = []
    generatedColumnKeys.forEach((key) => {
      generatedColumns.push([
        key,
        `${toTitle(key.replace('__generated__', ''))}`,
      ])
    })

    const groups = [
      {
        title: 'Uploaded Data',
        elements: baseColumns,
      },
      {
        title: 'Matched Data',
        elements: generatedColumns,
      },
    ]

    if (exportConfig.allowSuggestions) {
      groups.push({
        title: 'Suggested Data',
        elements: suggestionColumns,
      })
    }

    return groups
  }, [importData, exportConfig])

  const hasCompanyMatchHeader = useMemo(() => {
    return importData?.headers.some((h) =>
      ['company_match', 'primary_key'].includes(columnOptions[h.mapped]?.type)
    )
  }, [importData])

  const canAddTags = useMemo<[boolean, string[]]>(() => {
    const tagHeader = importData?.headers.find(
      (h) => columnOptions[h.mapped]?.type === 'tag_data'
    )
    const hasHeader = Boolean(tagHeader)

    const lenRowsWithoutTags =
      importData?.rows.filter((r) => r.__internal__tag_set === -1).length ?? 0

    const lenRowsWithTagValues =
      importData?.rows.filter((r) => r[tagHeader?.original as string] !== '')
        .length ?? 0

    const hasPendingTags = lenRowsWithoutTags >= lenRowsWithTagValues

    const ableTo: boolean = hasHeader && hasPendingTags
    const reasonsUnableTo: string[] = []
    if (ableTo) {
      return [ableTo, reasonsUnableTo]
    }

    if (!hasHeader) {
      reasonsUnableTo.push(
        `You need to assign a column to the "${columnOptions['tag_name'].name}" field.`
      )
    }
    if (!hasPendingTags) {
      reasonsUnableTo.push('Tags have already been imported for all rows.')
    }

    return [ableTo, reasonsUnableTo]
  }, [importData])

  const canAddDeals = useMemo<[boolean, string[]]>(() => {
    const dealHeader = importData?.headers.find(
      (h) => columnOptions[h.mapped]?.type === 'deal_data'
    )
    const hasHeader = Boolean(dealHeader)

    const lenRowsWithoutDeals =
      importData?.rows.filter((r) => r.__internal__deal_set === -1).length ?? 0

    const lenRowsWithDealValues =
      importData?.rows.filter((r) => r[dealHeader?.original as string] !== '')
        .length ?? 0

    const hasPendingDeals = lenRowsWithoutDeals >= lenRowsWithDealValues

    const ableTo: boolean = hasHeader && hasPendingDeals
    const reasonsUnableTo: string[] = []
    if (ableTo) {
      return [ableTo, reasonsUnableTo]
    }

    if (!hasHeader) {
      reasonsUnableTo.push(
        'You need to assign at least one column to a Deal field'
      )
    }
    if (!hasPendingDeals) {
      reasonsUnableTo.push('Deals have already been imported for all rows.')
    }

    return [ableTo, reasonsUnableTo]
  }, [importData])

  const canAddContacts = useMemo<[boolean, string[]]>(() => {
    const contactHeader = importData?.headers.find(
      (h) => columnOptions[h.mapped]?.type === 'contact_data'
    )
    const hasHeader = Boolean(contactHeader)

    const lenRowsWithoutContacts =
      importData?.rows.filter((r) => r.__internal__contact_set === -1).length ??
      0

    const lenRowsWithContactValues =
      importData?.rows.filter(
        (r) => r[contactHeader?.original as string] !== ''
      ).length ?? 0

    const hasPendingContacts =
      lenRowsWithoutContacts >= lenRowsWithContactValues

    const ableTo = hasHeader && hasPendingContacts
    const reasonsUnableTo: string[] = []

    if (ableTo) {
      return [ableTo, reasonsUnableTo]
    }

    if (!hasHeader) {
      reasonsUnableTo.push('You need to assign a column to a Contact field.')
    }

    if (!hasPendingContacts) {
      reasonsUnableTo.push('Contacts have already been imported for all rows.')
    }

    return [ableTo, reasonsUnableTo]
  }, [importData])

  const canAddCompanyNotes = useMemo<[boolean, string[]]>(() => {
    const companyNotesHeader = importData?.headers.find(
      (h) => columnOptions[h.mapped]?.type === 'company_notes'
    )
    const hasHeader = Boolean(companyNotesHeader)

    const lenRowsWithoutCompanyNotes =
      importData?.rows.filter((r) => r.__internal__company_notes_set === -1)
        .length ?? 0

    const lenRowsWithCompanyNotesValues =
      importData?.rows.filter(
        (r) => r[companyNotesHeader?.original as string] !== ''
      ).length ?? 0

    const hasPendingCompanyNotes =
      lenRowsWithoutCompanyNotes >= lenRowsWithCompanyNotesValues

    const ableTo = hasHeader && hasPendingCompanyNotes
    const reasonsUnableTo: string[] = []

    if (ableTo) {
      return [ableTo, reasonsUnableTo]
    }

    if (!hasHeader) {
      reasonsUnableTo.push(
        `You need to assign a column to the "${columnOptions['company_notes'].name}" field.`
      )
    }

    if (!hasPendingCompanyNotes) {
      reasonsUnableTo.push(
        'Company Notes have already been imported for all rows.'
      )
    }

    return [ableTo, reasonsUnableTo]
  }, [importData])

  const companySubcategories = useMemo(() => {
    return companyCategories?.reduce(
      (acc, c) => {
        c.sub_categories?.forEach((s) => {
          acc.push({
            label: (
              <p className="text-sm">
                {c.name} - {s.name}
              </p>
            ),
            value: s.id,
            category_id: c.id,
          })
        })
        return acc
      },
      [] as { label: any; value: number; category_id: number }[]
    )
  }, [companyCategories])

  const chainMergerAvailableSubcategories = useMemo<
    Record<number, string>
  >(() => {
    const uniqueSubcategories = new Set(
      importData?.rows.map(
        (r) => r.__internal__existing_chain_proxy_subcategory_id
      )
    )
    const subCats =
      companySubcategories?.filter((s) => uniqueSubcategories.has(s.value)) ??
      []
    const subCatMap: Record<number, string> = {}
    for (const s of subCats) {
      subCatMap[s.value] = s.label
    }
    return subCatMap
  }, [importData, companySubcategories])

  const mappedColumns = useMemo(() => {
    return importData?.headers.map((h) => h.mapped)
  }, [importData])

  const shouldShowBulkActions = useMemo(() => {
    return Object.keys(rowSelection).filter((r) => rowSelection[r]).length > 0
  }, [rowSelection])

  const selectedRowIds = useMemo(() => {
    return Object.keys(rowSelection).filter((r) => rowSelection[r])
  }, [rowSelection])

  const selectedSubcategoryFilter = useMemo(() => {
    return columnFiltering.find(
      (f) => f.id === '__internal__existing_chain_proxy_subcategory_id'
    )?.value
  }, [columnFiltering])

  /* -------------
  Effects
  ------------- */
  useEffect(() => {
    if (importPreview) {
      loadImportPreviewData(importPreview.data)
    }
    return () => {
      setImportData(null)
    }
  }, [importPreview])

  useEffect(() => {
    if (importPreviewStatus?.status === 'idle') {
      refetch()
    }
  }, [importPreviewStatus])

  /* -------------
  Field Handlers
  ------------- */
  function updateMap(original_header: string, mapped_column: string) {
    if (!importData) return
    const data = [...importData.headers]
    const header = data.find((h) => h.original === original_header)
    if (header) {
      header.mapped = mapped_column
    }
    setImportData({ ...importData, headers: data })
    setHasPendingChanges(true)
  }

  function updateMatchedDoor(
    row_id: string,
    matched_door: CSVRow['__internal__matched_door'],
    manual_match: boolean
  ) {
    if (!importData) return
    const data = [...importData.rows]
    const row = data.findIndex((r) => r.__internal__row_id === row_id)
    if (row !== -1) {
      data[row].__internal__matched_door = matched_door
      data[row].__internal__is_door_manual_match = manual_match
    }
    setImportData({ ...importData, rows: data })
    setHasPendingChanges(true)
  }

  function updateMatchedChain(
    row_id: string,
    matched_chain: CSVRow['__internal__matched_chain'],
    manual_match: boolean
  ) {
    if (!importData) return
    const data = [...importData.rows]
    const row = data.findIndex((r) => r.__internal__row_id === row_id)
    if (row !== -1) {
      data[row].__internal__matched_chain = matched_chain
      data[row].__internal__is_chain_manual_match = manual_match
    }
    setImportData({ ...importData, rows: data })
    setHasPendingChanges(true)
  }

  function updateIsCustomCompany(
    row_ids: string[],
    is_custom_company: boolean
  ) {
    if (!importData) return
    const data = [...importData.rows]
    for (const d of data) {
      if (row_ids.includes(d.__internal__row_id)) {
        d.__internal__is_custom_company = is_custom_company
      }
    }
    setImportData({ ...importData, rows: data })
    setHasPendingChanges(true)
  }

  function updateCustomCompanyCategory(
    row_ids: string[],
    category_id: number,
    subcategory_id: number | null
  ) {
    if (!importData) return
    const data = [...importData.rows]
    for (const d of data) {
      if (row_ids.includes(d.__internal__row_id)) {
        d.__internal__custom_company_category_id = category_id
        d.__internal__custom_company_sub_category_id = subcategory_id
        d.__internal__is_custom_company = true
      }
    }
    setImportData({ ...importData, rows: data })
    setHasPendingChanges(true)
  }

  const startManualSearch = (rowId: string) => {
    setSelectedRowId(rowId)
  }

  const handleManualSearchSelect = (
    door: DoorSearchResult | null,
    chain: ChainSearchResult | null
  ) => {
    if (selectedRowId === null || !importData) return

    if (door) {
      updateMatchedDoor(selectedRowId, door, true)
      updateMatchedChain(selectedRowId, chain, true)
    } else if (chain) {
      updateMatchedChain(selectedRowId, chain, true)
    }

    setSelectedRowId(null)
  }

  /* -------------
  Actions
  ------------- */
  async function handleSave(localImportData?: ImportData) {
    setIsSaving(true)

    let dataToSave: ImportData | null = null
    if (localImportData) {
      dataToSave = { ...localImportData }
    } else if (importData) {
      dataToSave = { ...importData }
    }

    if (!dataToSave) {
      return
    }

    const data = toRowsJSON(dataToSave)

    try {
      // Update
      if (props.importNumber) {
        await updateImportPreview(props.importNumber, {
          import_data: { data },
        })
        refetch()
      } else {
        // Create
        const rawDataString = await rawFile?.text()
        const fileName = rawFile?.name
        const response = await createImportPreview({
          import_data: {
            data,
            raw_data: rawDataString ?? '',
            name: fileName ?? '',
          },
        })
        navigate(`/importer/${response.import_number}`, { replace: true })
        return response.import_number
      }
      setHasPendingChanges(false)
      return props.importNumber
    } catch {
      toast.error('Error saving import preview')
    } finally {
      setIsSaving(false)
    }
  }

  async function handleMatch() {
    if (!importData) return
    if (!props.importNumber) return

    if (!hasCompanyMatchHeader) {
      toast.error(
        'In order to match, you must map at least one company-related or ID column.'
      )
      return
    }

    const confirmed = await confirm(
      'The matching process will attempt to automatically find matches for your imported rows in our database based on the columns you have selected. This process may take several minutes to complete.',
      'Start Matching',
      'The import experience will be paused until the matching process is complete.'
    )
    if (!confirmed) {
      return
    }

    setIsMatching(true)
    try {
      await handleSave()
      await performMatch(props.importNumber)
      refetchStatus()
    } catch (error) {
      console.error(error)
    } finally {
      setIsMatching(false)
      setHasPendingChanges(true)
    }
  }

  async function handleSetCompanies() {
    if (!importData) return
    if (!props.importNumber) return
    setIsSettingCompanies(true)
    try {
      await handleSave()
      const data = await performSetCompanies(props.importNumber)
      loadImportPreviewData(data.data)
    } catch (error) {
      console.error(error)
    } finally {
      setIsSettingCompanies(false)
    }
  }

  async function handleSetTags() {
    if (!importData) return
    if (!props.importNumber) return
    setIsSettingTags(true)
    try {
      await handleSave()
      const data = await performSetTags(props.importNumber)
      loadImportPreviewData(data.data)
    } catch (error) {
      console.error(error)
    } finally {
      setIsSettingTags(false)
    }
  }

  async function handleSetDeals() {
    if (!importData) return
    if (!props.importNumber) return

    setIsSettingDeals(true)
    try {
      await handleSave()

      // Validate Deal Account Owners
      if (importData.headers.some((h) => h.mapped === 'deal_account_owner')) {
        const data = await validateDealAccountOwners(props.importNumber)
        if (!data.success) {
          setValidateModalData({
            title: 'Invalid Deal Account Owners',
            missing: data.missing,
            found: data.found,
            options:
              users?.map((u) => ({
                label: u.email,
                value: u.email,
              })) ?? [],
            itemName: 'Deal Account Owner',
            columnName: 'deal_account_owner',
          })
          return
        }
      }

      // Validate Deal Sales Stages
      if (importData.headers.some((h) => h.mapped === 'deal_sales_stage')) {
        const data = await validateDealSalesStages(props.importNumber)
        if (!data.success) {
          setValidateModalData({
            title: 'Invalid Deal Sales Stages',
            missing: data.missing,
            found: data.found,
            options:
              salesStages?.map((s) => ({
                label: s.stage,
                value: s.stage,
              })) ?? [],
            itemName: 'Deal Sales Stage',
            columnName: 'deal_sales_stage',
          })
          return
        }
      }

      // Validate Deal Product
      if (!importData.headers.some((h) => h.mapped === 'deal_product')) {
        setShowSelectDealProductModal(true)
        return
      }

      const data = await performSetDeals(props.importNumber)
      loadImportPreviewData(data.data)
    } catch (error) {
      console.error(error)
    } finally {
      setIsSettingDeals(false)
    }
  }

  async function handleSetContacts() {
    if (!importData) return
    if (!props.importNumber) return
    setIsSettingContacts(true)
    try {
      await handleSave()
      const data = await performSetContacts(props.importNumber)
      loadImportPreviewData(data.data)
    } catch (error) {
      console.error(error)
    } finally {
      setIsSettingContacts(false)
    }
  }

  async function handleSetCompanyNotes() {
    if (!importData) return
    if (!props.importNumber) return
    setIsSettingCompanyNotes(true)
    try {
      await handleSave()
      const data = await performSetCompanyNotes(props.importNumber)
      loadImportPreviewData(data.data)
    } catch (error) {
      console.error(error)
    } finally {
      setIsSettingCompanyNotes(false)
    }
  }

  function handleValidation(changes: ValidateModalRow[], columnName: string) {
    if (!importData) return
    const mutData = { ...importData }
    const header = mutData.headers.find((h) => h.mapped === columnName)
    if (!header) return

    for (const change of changes) {
      const changeValue =
        change.mapped === FB_REMOVE_CELL_VALUE ? null : change.mapped

      for (let i = 0; i < mutData.rows.length; i++) {
        const row = mutData.rows[i]
        if (row[header.original] === change.original) {
          mutData.rows[i][header.original] = changeValue
        }
      }
    }

    setImportData(mutData)
    setValidateModalData(null)
  }

  function handleSelectDealProduct(productName: string) {
    if (!importData) return
    const mutData = { ...importData }

    mutData.rows.forEach((row) => {
      row['__internal__deal_product'] = productName
    })

    mutData.headers.push({
      original: '__internal__deal_product',
      mapped: 'deal_product',
      label: 'Deal Product',
    })
    setImportData(mutData)

    setShowSelectDealProductModal(false)
  }

  function handleConfigureExport() {
    setShowConfigureExportModal(false)
    setShowExportModal(true)
  }

  const handleExport = useCallback(
    async (selectedColumns: string[]) => {
      if (!importData) return

      const data = importData.rows

      // Transform rows to include exploded matched fields
      const transformedRows: Record<string, any>[] = []
      for (const row of data) {
        const baseRow: Record<string, any> = {}

        function addColumn(key: string, value: any, row: Record<string, any>) {
          if (selectedColumns.includes(key)) {
            row[key] = value
          }
        }

        // Remove nested objects
        for (const key in row) {
          if (
            [
              '__internal__row_id',
              '__internal__existing_chain_proxy_id',
              '__internal__existing_chain_proxy_type',
              '__internal__existing_chain_proxy_subcategory_id',
              '__internal__set_chain_proxy_id',
              '__internal__matched_door',
              '__internal__matched_chain',
              '__internal__suggested_doors',
              '__internal__suggested_chains',
              '__internal__is_custom_company',
              '__internal__custom_company_category_id',
              '__internal__custom_company_sub_category_id',
              '__internal__ready',
              '__internal__is_chain_merged',
              '__internal__is_door_manual_match',
              '__internal__is_chain_manual_match',
              '__internal__tag_set',
              '__internal__deal_set',
              '__internal__contact_set',
              '__internal__company_notes_set',
              '__internal__deal_product',
              '__internal__matched_chain_pk',
              '__internal__matched_chain_type',
            ].includes(key) ||
            key.startsWith('matching__')
          ) {
            continue
          }
          baseRow[key] = row[key]
        }

        // ### SUGGESTIONS COLUMNS ###

        // Add blank columns for completeness
        chainSuggestionKeys.forEach((key) => {
          addColumn(`__suggestion__chain__${key}`, '', baseRow)
        })
        doorSuggestionKeys.forEach((key) => {
          addColumn(`__suggestion__door__${key}`, '', baseRow)
        })

        if (
          // Show only if row is not set - i.e. not imported
          row.__internal__set_chain_proxy_id === -1 &&
          exportConfig.allowSuggestions
        ) {
          // Add the first suggestions to base row
          chainSuggestionKeys.forEach((key) => {
            addColumn(
              `__suggestion__chain__${key}`,
              row.__internal__suggested_chains?.[0]?.[key] ?? '',
              baseRow
            )
          })
          doorSuggestionKeys.forEach((key) => {
            addColumn(
              `__suggestion__door__${key}`,
              row.__internal__suggested_doors?.[0]?.[key] ?? '',
              baseRow
            )
          })

          // Add extra suggestions as new rows
          const extraSuggestionRows = []
          if (
            row.__internal__suggested_chains?.length &&
            row.__internal__suggested_chains.length > 1
          ) {
            for (const s of row.__internal__suggested_chains.slice(
              1,
              exportConfig.suggestionAmount
            )) {
              const rowWithSuggestions = { ...baseRow }
              chainSuggestionKeys.forEach((key) => {
                addColumn(
                  `__suggestion__chain__${key}`,
                  s[key],
                  rowWithSuggestions
                )
              })
              extraSuggestionRows.push(rowWithSuggestions)
            }
          }
          if (
            row.__internal__suggested_doors?.length &&
            row.__internal__suggested_doors.length > 1
          ) {
            for (const s of row.__internal__suggested_doors.slice(
              1,
              exportConfig.suggestionAmount
            )) {
              const rowWithSuggestions = { ...baseRow }
              doorSuggestionKeys.forEach((key) => {
                addColumn(
                  `__suggestion__door__${key}`,
                  s[key],
                  rowWithSuggestions
                )
              })
              extraSuggestionRows.push(rowWithSuggestions)
            }
          }
          if (extraSuggestionRows.length > 0) {
            transformedRows.push(...extraSuggestionRows)
          }
        }

        // ### MATCHED COLUMNS ###
        // Add blank columns
        addColumn('__generated__firstbite_chain_id', '', baseRow)
        addColumn('__generated__firstbite_door_id', '', baseRow)
        addColumn(
          '__generated__chain_name',
          row.__internal__matched_chain?.name ?? '',
          baseRow
        )
        addColumn(
          '__generated__chain_domain',
          row.__internal__matched_chain?.domain ?? '',
          baseRow
        )
        addColumn(
          '__generated__chain_size',
          row.__internal__matched_chain?.count ?? '',
          baseRow
        )

        // Row is set - Add FB ID and Door ID
        if (row.__internal__set_chain_proxy_id !== -1) {
          // FB ID
          // Get Prefixed Firstbite ID e.g. RE12312
          let firstbiteId =
            (row.__internal__matched_chain?.type ?? '') +
            (row.__internal__matched_chain?.item_id ?? '')
          if (row.__internal__is_custom_company) {
            firstbiteId = 'UG' + row.__internal__set_chain_proxy_id
          }
          addColumn('__generated__firstbite_chain_id', firstbiteId, baseRow)

          // Get Door ID e.g. 12312
          if (row.__internal__matched_door) {
            addColumn(
              '__generated__firstbite_door_id',
              row.__internal__matched_door.item_id,
              baseRow
            )
          }
        }

        transformedRows.push(baseRow)
      }

      // Convert to CSV
      const csv = Papa.unparse(transformedRows)

      // Create and trigger download
      const blob = new Blob([csv], { type: 'text/csv;charset=utf-8;' })
      const link = document.createElement('a')
      const url = URL.createObjectURL(blob)
      link.setAttribute('href', url)
      link.setAttribute('download', 'exported_data.csv')
      link.style.visibility = 'hidden'
      document.body.appendChild(link)
      link.click()
      document.body.removeChild(link)
    },
    [importData, exportConfig]
  )

  function handleFileUpload(file: File | null) {
    setRawFile(file)
    if (!file) return

    setIsUploading(true)
    Papa.parse<CSVRow>(file, {
      header: true,
      skipEmptyLines: 'greedy',
      delimitersToGuess: [';', ','],
      complete: (results) => {
        const data = results.data.map((row) => {
          return {
            ...Object.fromEntries(
              Object.entries(row).map(([key, value]) => [
                key,
                typeof value === 'string' ? value.slice(0, 200) : undefined,
              ])
            ),
          }
        }) as CSVRow[]
        const importData = {
          rows: data,
          headers:
            results.meta.fields?.map((h) => ({
              original: h,
              mapped: 'ignore',
              label: undefined,
            })) ?? [],
        }
        setImportData(importData)
        setShowHeaderMappingModal(true)
        setIsUploading(false)
      },
      error: (error) => {
        console.error('Error parsing CSV:', error)
        setIsUploading(false)
      },
    })
  }

  async function handleSetUnmatchedChains() {
    if (!importData) return
    if (!props.importNumber) return
    try {
      setIsSettingUnmatchedChains(true)
      await handleSave()
      await setUnmatchedChains(props.importNumber)
      refetchStatus()
    } catch (error) {
      console.error(error)
    } finally {
      setIsSettingUnmatchedChains(false)
    }
  }

  function loadImportPreviewData(data: ImportDataRows) {
    setImportData(fromRowsJSON(data))
  }

  function setSubcategoryFilter(subcategoryId: number) {
    if (Number.isNaN(subcategoryId)) {
      clearSubcategoryFilter()
      return
    }

    setColumnFiltering([
      {
        id: '__internal__existing_chain_proxy_subcategory_id',
        value: subcategoryId,
      },
    ])
  }

  function clearSubcategoryFilter() {
    setColumnFiltering([])
  }

  return {
    // Data
    rawFile,
    importPreview,
    importData,
    products,
    isLoading,
    isFetching,
    isLoadingStatus,

    // Modal states
    showHeaderMappingModal,
    setShowHeaderMappingModal,
    showSelectDealProductModal,
    selectedDealProduct,
    setSelectedDealProduct,
    showConfigureExportModal,
    setShowConfigureExportModal,

    // Loading states
    isSaving,
    isUploading,
    isMatching,
    isSettingCompanies,
    isSettingTags,
    isSettingDeals,
    isSettingContacts,
    isSettingCompanyNotes,
    isSettingUnmatchedChains,

    // Form states
    hasPendingChanges,
    selectedTab,
    setSelectedTab,
    rowSelection,
    setRowSelection,
    validateModalData,
    importPreviewStatus,
    setShowSelectDealProductModal,
    showExportModal,
    setShowExportModal,

    // Computed values
    companySubcategories,
    mappedColumns,
    tableData,
    canAddTags,
    canAddDeals,
    canAddContacts,
    canAddCompanyNotes,

    // Handlers
    updateMap,
    updateIsCustomCompany,
    updateCustomCompanyCategory,
    startManualSearch,
    handleManualSearchSelect,
    handleMatch,
    handleSetCompanies,
    handleSetTags,
    handleSetDeals,
    handleSetContacts,
    handleSetCompanyNotes,
    handleValidation,
    handleSelectDealProduct,
    handleExport,
    handleFileUpload,
    handleSetUnmatchedChains,
    updateMatchedChain,
    updateMatchedDoor,
    handleSave,
    setValidateModalData,
    setSelectedRowId,
    selectedRowId,
    shouldShowBulkActions,
    selectedRowIds,
    chainMergerAvailableSubcategories,
    handleConfigureExport,

    // Column Filtering
    columnFiltering,
    setSubcategoryFilter,
    clearSubcategoryFilter,
    selectedSubcategoryFilter,

    // Export
    exportColumns,
    exportConfig,
    setExportConfig,
  }
}

function toRowsJSON(data: ImportData): ImportDataRows {
  if (!data.rows.length)
    return {
      headers: [],
      rows: {
        columns: [],
        rows: [],
      },
    }
  const columns = Object.keys(data.rows[0])
  const rows = data.rows.map((obj) => columns.map((key) => obj[key]))
  return {
    headers: data.headers,
    rows: {
      columns,
      rows,
    },
  }
}

function fromRowsJSON(data: ImportDataRows): ImportData {
  return {
    headers: data.headers,
    rows:
      data.rows?.rows?.map(
        (row) =>
          Object.fromEntries(
            (data.rows?.columns || []).map((key, i) => [key, row[i]])
          ) as CSVRow
      ) || [],
  }
}

export const columnTypeColors: Record<ColumnType, ColumnColors> = {
  // Field that is used to match the company exactly, rendering other matching fields irrelevant
  primary_key: {
    background: '#E3F2FD',
    text: '#1E40AF',
  },
  // Field that is used to match the company
  company_match: {
    background: '#F3E5F5',
    text: '#6B21A8',
  },
  // Regular data
  tag_data: {
    background: '#ffffff',
    text: '#000000',
  },
  deal_data: {
    background: '#ffffff',
    text: '#000000',
  },
  contact_data: {
    background: '#ffffff',
    text: '#000000',
  },
  company_notes: {
    background: '#FFF8E1',
    text: '#F57F17',
  },
  // Ignore column
  ignore: {
    background: 'transparent',
    text: '#4B556340',
  },
  display_only: {
    background: '#ffffff',
    text: '#000000',
  },
}

export const columnOptions: Record<string, ColumnOption> = {
  firstbite_id: {
    name: 'First Bite Chain ID',
    type: 'primary_key',
  },
  door_id: {
    name: 'Door ID',
    type: 'primary_key',
  },
  company_name: {
    name: 'Company Name',
    type: 'company_match',
  },
  company_phone: {
    name: 'Company Phone',
    type: 'company_match',
  },
  company_opp_type: {
    name: 'Company Type',
    type: 'company_match',
  },
  company_address_state: {
    name: 'Company State',
    type: 'company_match',
  },
  company_address_city: {
    name: 'Company City',
    type: 'company_match',
  },
  company_address_zip: {
    name: 'Company Zip',
    type: 'company_match',
  },
  company_address_country: {
    name: 'Company Country',
    type: 'company_match',
  },
  company_address_street: {
    name: 'Company Address',
    type: 'company_match',
  },
  company_address_street_2: {
    name: 'Company Address 2',
    type: 'company_match',
  },
  company_domain: {
    name: 'Company Domain',
    type: 'company_match',
  },
  company_full_address: {
    name: 'Company Full Address',
    type: 'company_match',
  },
  tag_name: {
    name: 'Tag Name',
    type: 'tag_data',
  },
  deal_sales_stage: {
    name: 'Deal Sales Stage',
    type: 'deal_data',
  },
  deal_account_owner: {
    name: 'Deal Account Owner Email',
    type: 'deal_data',
  },
  deal_product: {
    name: 'Deal Product',
    type: 'deal_data',
  },
  contact_full_name: {
    name: 'Contact Full Name',
    type: 'contact_data',
  },
  contact_first_name: {
    name: 'Contact First Name',
    type: 'contact_data',
  },
  contact_last_name: {
    name: 'Contact Last Name',
    type: 'contact_data',
  },
  contact_phone: {
    name: 'Contact Phone',
    type: 'contact_data',
  },
  contact_work_email: {
    name: 'Contact Work Email',
    type: 'contact_data',
  },
  contact_personal_email: {
    name: 'Contact Personal Email',
    type: 'contact_data',
  },
  contact_generic_email: {
    name: 'Contact Generic Email',
    type: 'contact_data',
  },
  contact_linkedin_url: {
    name: 'Contact LinkedIn URL',
    type: 'contact_data',
  },
  contact_title: {
    name: 'Contact Title',
    type: 'contact_data',
  },
  contact_label: {
    name: 'Contact Label',
    type: 'contact_data',
  },
  company_notes: {
    name: 'Company Notes',
    type: 'company_notes',
  },
}

const chainSuggestionKeys = [
  'name',
  'domain',
  'count',
  'item_id',
  'type',
  'score',
] as Array<keyof ChainSearchResult>

const doorSuggestionKeys = [
  'item_id',
  'name',
  'domain',
  'latitude',
  'longitude',
  'address',
  'city',
  'state',
  'country',
  'zipcode',
  'chain_id',
  'chain_name',
  'chain_size',
  'type',
  'score',
] as Array<keyof DoorSearchResult>

const generatedColumnKeys = [
  '__generated__firstbite_chain_id',
  '__generated__firstbite_door_id',
  '__generated__chain_name',
  '__generated__chain_domain',
  '__generated__chain_size',
]
